import { useEffect, type ReactNode } from 'react'
// Format a "MMM yyyy" label in UTC so SSR (UTC server) and hydration (visitor's
// local zone) render the identical string and don't trip a React #418 mismatch.
const monthYearUTC = (d: Date) =>
  d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', year: 'numeric' })
import { analytics } from '../utils/analytics'
import { tenure } from '../utils/tenure'

// Tipalti — current role, rendered LinkedIn-style with a Full-time tag.
const tipalti = {
  title: "Staff Software Engineer",
  company: "Tipalti",
  location: "London, UK",
  type: "Full-time",
  startdate: "2024-06-01",
  enddate: null,
  current: true,
  companyDescription: "Fintech automating accounts payable and procurement for global businesses.",
  bullets: [
    "Architected a new Vue/React design system, reducing frontend delivery time across 12 teams from **3 months to under 2 weeks**",
    "Led the implementation of inline editing, contributing to a **20-point increase in NPS**",
    "Designed a resilient Payee–Vendor Sync platform, reducing latency to **under 3 seconds** and cutting production issues by **10 per week**",
    "Led the phoenix team for migration of **200+ enterprise customers** from a legacy platform to a new architecture",
    "Designed a new user management and tenancy platform, enabling faster product delivery and future scalability; Janus was applied by **6 different domains**",
    "Improved developer productivity by introducing Turborepo, AI-assisted development, and E2E testing, reducing lead time from **10 days to 40 minutes**",
    "Mentored engineering teams on LLMs, Claude Code, and AI adoption across the London office",
    "Optimised critical APIs, reducing latency from **10 seconds to under 2 seconds**",
  ],
  stack: "TypeScript, React, Next.js, Node.js, GraphQL, Apollo, AWS, Kubernetes, Kafka, RabbitMQ, Docker, Terraform, Datadog, CI/CD, Microservices, FinTech",
}

const jobs = [
  { id: 2, title: "Lead Software Engineer", company: "Mecca Brands", location: "Melbourne, Australia", startdate: "2023-01-01", enddate: "2024-03-01", current: false, companyDescription: "Australia's leading beauty retailer.", bullets: ["Served **~20% of business traffic** via a **micro-frontend architecture of 10+ Next.js apps** I designed, plus BFF layers for mobile", "Lifted **NPS 5 points** by cutting **5s** off the booking journey", "Led **6 engineers and testers** replatforming Mecca's web stack off legacy, shipping to production", "Built the **payments and checkout** flows end to end, plus the services domain and **Sitecore** integration"], stack: "TypeScript, React, Next.js, Node.js, Sitecore, Micro-frontends, AWS, Lambda" },
  { id: 3, title: "Lead Software Engineer", company: "IE Company", location: "Melbourne, Australia", startdate: "2021-08-01", enddate: "2023-01-01", current: false, companyDescription: "IE Company builds digital products for enterprise brands.", bullets: ["Cut **Toyota Australia's** LCP **~30%** via bundle code-splitting and **Core Web Vitals** work, plus new vehicle features and a **Sitecore CMS**", "Launched **Card.gift**, a full-stack gift-card marketplace, from scratch, owning backend, frontend, and payments", "Shipped an **Australia Post** in-store tablet app through a **BFF layer** I stood up", "Led a **5-engineer** product team for **Toyota** and **Australia Post**, owning direction and delivery"], stack: "TypeScript, React, NextJS, Node.js, Sitecore, AWS" },
  { id: 4, title: "Senior Software Engineer", company: "Sportsbet", location: "Melbourne, Australia", startdate: "2019-04-01", enddate: "2021-08-01", current: false, companyDescription: "Australia's leading online betting platform.", bullets: ["Cut **CI/CD time to under an hour** and enabled real-time odds by re-architecting odds handling to **stream events between services**", "Built **BetWithMate** social betting (via BetEasy, acquired by Sportsbet); shipped **multi-bet features** and contributed to the **design system** on the racing team"], stack: "React, Node.js, AWS, Kubernetes, Docker, CI/CD" },
  { id: 5, title: "Senior Front-end → Full-Stack Engineer", company: "CHE Proximity", location: "Melbourne, Australia", startdate: "2017-03-01", enddate: "2019-04-01", current: false, companyDescription: "CHE Proximity is a leading digital agency.", bullets: ["Built Mazda.com.au end to end, starting on React/Redux frontend then moving into full-stack Node.js, and was promoted to full-stack lead owning architecture and code review", "Led a Node.js survey-platform initiative and mentored frontend engineers"], stack: "React, Redux, Node.js, TypeScript, Webpack" },
  { id: 9, title: "Senior Backend Developer", company: "NetBarg", location: "Tehran, Iran", startdate: "2015-04-01", enddate: "2016-03-01", current: false, companyDescription: "Early-stage daily-deals startup (Groupon-style); sites served 1M+ daily users.", bullets: ["Led 20 developers across Netbarg.com and Tik8.com, each serving 1M+ daily visitors", "Introduced Scrum and built backend services with PHP frameworks and Node.js"], stack: "PHP, Node.js, AngularJS" },
  { id: 10, title: "Co-Founder", company: "Atys", location: "Tehran, Iran", startdate: "2010-01-01", enddate: "2015-04-01", current: false, companyDescription: "Early-stage startup I co-founded, grew to 10+ engineers, shipped 100+ products.", bullets: ["Grew the team to 10+ engineers and ran delivery, hiring, and client work", "Shipped 100+ products end to end across web and backend"], stack: "PHP, Java, JavaScript, SQL" },
  { id: 11, title: "Senior Web Developer", company: "Rahbaran System Rastak", location: "Tehran, Iran", startdate: "2009-03-01", enddate: "2010-09-01", current: false },
  { id: 12, title: "Web Developer", company: "Segal Control Co.", location: "Tehran, Iran", startdate: "2008-08-01", enddate: "2009-03-01", current: false },
]

// External speaking only. Internal company brown-bags were cut: they read as filler
// to hiring managers and dilute the genuinely-selected conference slots.
const talks = [
  { id: 1, title: "MicroFrontEnd at Scale", event: "Web Directions", year: 2024, url: "https://webdirections.org/code23/speakers/ehsan-gazar.php" },
  { id: 3, title: "Do You Believe in Magic? Time to Move to Remix.", event: "MelbJS", year: 2023 },
  { id: 9, title: "Gazar Breakpoint, engineering and system design talks", event: "YouTube", year: 2026, url: "https://www.youtube.com/@gazarbreakpoint" },
]

const MUTED = "#5b6166"
const NAVY = "#00205B"
const RED = "#E4002B"
// gazar.dev heritage accent range — used to tint sections/labels by topic family.
const TEAL = "#0E7C86"
const SLATE = "#4F6280"
const FOREST = "#2C7A51"
const BURGUNDY = "#8A1B3D"
const GOLD = "#C0902F"
const CORAL = "#D2553B"

function dateRange(job: { startdate: string; enddate: string | null; current: boolean }) {
  const start = monthYearUTC(new Date(job.startdate))
  const end = job.current ? "Present" : job.enddate ? monthYearUTC(new Date(job.enddate)) : ""
  return `${start} – ${end}`
}

// Renders **text** segments as semibold for scannable keyword emphasis.
function withEmphasis(text: string) {
  return text.split('**').map((part, i) => (i % 2 === 1 ? <strong key={i} className="font-semibold">{part}</strong> : part))
}

function Section({ children, color = NAVY, first = false }: { children: ReactNode; color?: string; first?: boolean }) {
  return (
    <h2
      className={`${first ? "mt-3" : "mt-7"} mb-3 px-3 py-1.5 rounded text-[12px] font-bold uppercase tracking-[0.16em]`}
      style={{ backgroundColor: color, color: "#ffffff" }}
    >
      {children}
    </h2>
  )
}

export default function ResumePrincipalPage() {
  useEffect(() => {
    analytics.capture('resume_viewed', {
      host: typeof window !== 'undefined' ? window.location.hostname : undefined,
    })
  }, [])

  return (
    <>
      <main className="resume bg-white">
        <style dangerouslySetInnerHTML={{ __html: `
          .resume { color:#1a1a1a; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif; -webkit-font-smoothing:antialiased; text-rendering:optimizeLegibility; }
          .resume h2, .resume .job-title, .resume .accent-rule { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
          .resume h1, .resume h2, .resume h3 { color:#1a1a1a; font-family:inherit; }
          .resume a { color:#00205B; }
          @media print {
            @page { margin: .7in; }
            html, body { background:#fff !important; }
            .resume { font-size:10pt; line-height:1.4; }
            .resume a { color:inherit !important; text-decoration:none !important; }
            .resume .page-break { break-before:page !important; page-break-before:always !important; }
            .resume .job { break-inside:avoid; page-break-inside:avoid; }
            .resume h2 { break-after:avoid; page-break-after:avoid; }
          }
        ` }} />
        <div className="mx-auto max-w-4xl px-8 py-9">
          {/* Header */}
          <header className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <h1 className="text-[28px] font-bold tracking-tight leading-[1.1]" style={{ color: NAVY }}>Ehsan Gazar</h1>
              <div className="accent-rule mt-2 h-[3px] w-12 rounded-full" style={{ backgroundColor: RED }} />
              <p className="mt-1.5 text-[15px] font-medium" style={{ color: NAVY }}>Staff Software Engineer</p>
            </div>
            <p className="text-[13px] leading-[1.45] sm:text-right shrink-0" style={{ color: MUTED }}>
              London, UK<br />
              <a href="tel:+447405745563" className="underline">+44 7405 745563</a> · <a href="mailto:me@gazar.dev" className="underline">me@gazar.dev</a><br />
              <a href="https://gazar.dev" className="underline">gazar.dev</a> · <a href="https://github.com/ehsangazar" className="underline">github.com/ehsangazar</a><br />
              <a href="https://linkedin.com/in/gazar" className="underline">linkedin.com/in/gazar</a>
            </p>
          </header>

          {/* Summary */}
          <Section first>Summary</Section>
          <p className="text-[14px] leading-[1.5]">
I'm a full-stack software engineer with over a decade of experience building scalable software across fintech, retail, wagering, enterprises, and startups.
          </p>
          <p className="mt-2 text-[14px] leading-[1.5]">
I'm passionate about solving complex engineering problems, identifying high-impact initiatives, and turning ideas into production through pragmatic architecture, HLDs, ADRs, diagrams and technical leadership. My focus is on building solutions that are scalable, maintainable, and future-proof. AI is reshaping software engineering, and I'm committed to adopting it early to build better products and help teams work more effectively. I also enjoy mentoring engineers through one-on-ones, design reviews, guild sessions, and pair programming, helping them grow their skills and confidence.
          </p>

          {/* Core Technologies */}
          <Section color={TEAL}>Core Technologies</Section>
          <dl className="text-[14px] leading-[1.5] space-y-1">
            <div><span className="font-semibold" style={{ color: TEAL }}>Backend:</span> Node.js, REST/GraphQL/gRPC, distributed systems, microservices, event-driven (Kafka, RabbitMQ), SQL &amp; NoSQL databases.</div>
            <div><span className="font-semibold" style={{ color: SLATE }}>Frontend:</span> React, Next.js, TypeScript, micro-frontends, design systems.</div>
            <div><span className="font-semibold" style={{ color: FOREST }}>Cloud &amp; infra:</span> AWS, Kubernetes, Docker, Terraform, CI/CD, Datadog, Coralogix.</div>
            <div><span className="font-semibold" style={{ color: CORAL }}>Tooling &amp; DX:</span> Monorepos, Vitest, Oxlint, Oxfmt, GitHub Actions.</div>
            <div><span className="font-semibold" style={{ color: BURGUNDY }}>AI:</span> LLM integration, RAG, agents and internal developer tooling.</div>
            <div><span className="font-semibold" style={{ color: GOLD }}>Practice:</span> domain-driven design, system design, performance, reliability, mentoring.</div>
          </dl>

          {/* Experience */}
          <Section>Experience</Section>
          <div className="space-y-5">
            {/* Tipalti — current role (LinkedIn-style) */}
            <div className="job">
              <div className="job-title border-l-[3px] pl-3 flex items-start justify-between gap-4" style={{ borderColor: RED }}>
                <div>
                  <div className="text-[13.5px] font-bold" style={{ color: NAVY }}>{tipalti.title}</div>
                  <div className="text-[12px] font-medium" style={{ color: MUTED }}>{tipalti.company} · {tipalti.location} · {tipalti.type}</div>
                </div>
                <div className="text-[12px] text-right whitespace-nowrap shrink-0" style={{ color: MUTED }}>{dateRange(tipalti)} · {tenure(tipalti)}</div>
              </div>
              <p className="text-[13px] italic mt-1" style={{ color: MUTED }}>{tipalti.companyDescription}</p>
              <ul className="mt-1 text-[14px] leading-[1.45] list-disc list-outside pl-5 space-y-0.5">
                {tipalti.bullets.map((b, i) => <li key={i} className="marker:text-[#00205B]">{withEmphasis(b)}</li>)}
              </ul>
              <p className="text-[13px] mt-1" style={{ color: MUTED }}>
                <span className="font-semibold" style={{ color: NAVY }}>Tech:</span> {tipalti.stack}
              </p>
            </div>

            {jobs.slice(0, 8).map((job) => (
              <div key={job.id} className={`job${job.id === 2 ? ' page-break' : ''}`}>
                <div className="job-title border-l-[3px] pl-3 flex items-start justify-between gap-4" style={{ borderColor: RED }}>
                  <div>
                    <div className="text-[13.5px] font-bold" style={{ color: NAVY }}>{job.title}</div>
                    <div className="text-[12px] font-medium" style={{ color: MUTED }}>{job.company} · {job.location}</div>
                  </div>
                  <div className="text-[12px] text-right whitespace-nowrap shrink-0" style={{ color: MUTED }}>{dateRange(job)} · {tenure(job)}</div>
                </div>
                {'companyDescription' in job && job.companyDescription && (job.id <= 4 || job.id === 9 || job.id === 10) && (
                  <p className="text-[13px] italic mt-1" style={{ color: MUTED }}>{job.companyDescription}</p>
                )}
                {'bullets' in job && job.bullets && job.bullets.length > 0 && job.id <= 4 && (
                  <ul className="mt-1 text-[14px] leading-[1.45] list-disc list-outside pl-5 space-y-0.5">
                    {job.bullets.map((b, i) => <li key={i} className="marker:text-[#00205B]">{withEmphasis(b)}</li>)}
                  </ul>
                )}
                {'stack' in job && job.stack && job.id <= 4 && (
                  <p className="text-[13px] mt-1" style={{ color: MUTED }}>
                    <span className="font-semibold" style={{ color: NAVY }}>Tech:</span> {job.stack}
                  </p>
                )}
              </div>
            ))}
            <p className="text-[13px]" style={{ color: MUTED }}>
              <span className="font-semibold" style={{ color: NAVY }}>Earlier roles:</span> Lead JavaScript Developer, Round Table Apps, Sydney (2016–2017); Lead Full-Stack Developer, PersianGig (2016); Senior Web Developer, Rahbaran System Rastak (2009–2010); Web Developer, Segal Control Co. (2008–2009).
            </p>
          </div>

          {/* Education */}
          <div className="page-break" aria-hidden="true" />
          <Section color={FOREST}>Education</Section>
          <h3 className="text-[14.5px] font-semibold">BSc in Computer Science</h3>
          <p className="text-[13px] mt-0.5" style={{ color: MUTED }}>
            K. N. Toosi University of Technology, Tehran · 2008–2013
          </p>

          {/* Writing, Teaching & Community */}
          {/* Speaking */}
          <Section color={BURGUNDY}>Speaking</Section>
          <ul className="text-[14px] leading-[1.45] list-disc list-outside pl-5 space-y-0.5">
            {talks.map((talk) => (
              <li key={talk.id} className="marker:text-[#00205B]">
                {talk.title} <span style={{ color: MUTED }}>· {'url' in talk && talk.url ? <a href={talk.url} target="_blank" rel="noopener noreferrer" className="underline">{talk.event}</a> : talk.event}, {talk.year}</span>
              </li>
            ))}
          </ul>
        </div>
      </main>
    </>
  )
}
